import { SquareArrowOutUpRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import EditPositionModal from 'components/IncidentConfig/EditPositionModal';
import RosterResourceModal from 'components/IncidentConfig/roster/RosterResourceModal';
import { PlannedResourceRow } from 'components/IAP/hooks/assignmentListHooks';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import {
  useGetPositionsForIncident,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import type { PlannedResourceDraft } from './assignmentCardFormTypes';
import AssignmentCardResourceAttachmentDialog from './AssignmentCardResourceAttachmentDialog';
import { ICS204_ICON_BTN } from './assignmentCardStyles';

type PopoutKind = 'resource' | 'rosterResource' | 'personnel';

type PlannedResourcePopoutButtonProps = {
  plannedResource: PlannedResourceRow;
  /**
   * When supplied (edit form), 204A/list-item fields write into the draft
   * buffer instead of PUTting immediately.
   */
  onCommit?: (fields: Partial<PlannedResourceDraft>) => void;
  periodNumber?: number;
};

/**
 * Opens the incident-resource detail dialog, or the roster position modal when
 * the planned row stands for a roster seat (`position_id` on the resource).
 */
const PlannedResourcePopoutButton = ({
  plannedResource,
  onCommit,
  periodNumber,
}: PlannedResourcePopoutButtonProps) => {
  const { data: resourcesInIncident = [] } = useResourcesInIncident();
  const { data: positions = [] } = useGetPositionsForIncident();
  const { data: activeMembers = [] } = useGetRosterForIncident();
  const { data: draftMembers = [] } = useGetDraftRosterMembersForIncident();

  const [open, setOpen] = useState(false);

  const resourceInIncident = useMemo(
    () => resourcesInIncident.find((r) => r.id === plannedResource.id),
    [resourcesInIncident, plannedResource.id]
  );

  const positionId = resourceInIncident?.position_id ?? null;
  const position = useMemo(
    () =>
      positionId
        ? positions.find((candidate) => candidate.id === positionId)
        : undefined,
    [positions, positionId]
  );

  const kind: PopoutKind | null = useMemo(() => {
    if (!resourceInIncident) return null;
    if (!positionId) return 'resource';
    if (!position) return null;
    return position.isResourcePosition ? 'rosterResource' : 'personnel';
  }, [resourceInIncident, positionId, position]);

  const disabled = kind === null;

  return (
    <>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={`tw:h-8 tw:w-8 ${ICS204_ICON_BTN}`}
        title="Open resource or roster details"
        data-testid="ics204-planned-resource-popout"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          setOpen(true);
        }}
      >
        <SquareArrowOutUpRight className="tw:h-4 tw:w-4" />
      </Button>
      {open && kind === 'resource' ? (
        <AssignmentCardResourceAttachmentDialog
          onClose={() => setOpen(false)}
          data={plannedResource}
          onCommit={onCommit}
          periodNumber={periodNumber}
        />
      ) : null}
      {open && kind === 'rosterResource' && position ? (
        <RosterResourceModal
          show
          position={position}
          onClose={() => setOpen(false)}
        />
      ) : null}
      {open && kind === 'personnel' && positionId ? (
        <EditPositionModal
          show
          onHide={() => setOpen(false)}
          positionId={positionId}
          activeMembers={activeMembers}
          draftMembers={draftMembers}
        />
      ) : null}
    </>
  );
};

export default PlannedResourcePopoutButton;
